import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { newIdempotencyKey } from "../lib/collaborators";
import FicheFieldsForUser, { defaultFicheDraft, isFicheDraftValid } from "../components/FicheFieldsForUser";
import api, { errorMessage } from "../lib/api";
import { suggestedRoles } from "../lib/skills";
import SuggestedRoleHint from "../components/SuggestedRoleHint";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Eye, EyeOff, Mail, ShieldCheck, Lightbulb, Check, RefreshCw } from "lucide-react";

// Ni trop simple (que des minuscules) ni trop complexe (pas de symboles à
// déchiffrer au clavier) : pensé pour un mot de passe temporaire transmis
// une fois puis changé par l'utilisateur à sa première connexion.
const PASSWORD_CHARS = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789"; // sans 0/O/1/l/I
function generatePassword(length = 10) {
  let out = "";
  for (let i = 0; i < length; i++) {
    out += PASSWORD_CHARS[Math.floor(Math.random() * PASSWORD_CHARS.length)];
  }
  return out;
}

export default function UserFormPage() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  // arriving from a collaborator fiche that has no account: the new account is linked to that fiche right after creation
  const forCollaborator = useLocation().state?.forCollaborator || null;
  const collabOn = me?.role === "admin" && !!me?.features?.collaborators && !forCollaborator;
  const [withFiche, setWithFiche] = useState(false);
  const [ficheDraft, setFicheDraft] = useState(defaultFicheDraft());
  const [ficheKey] = useState(() => newIdempotencyKey());
  const [roles, setRoles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: forCollaborator?.name || "", email: forCollaborator?.email || "", password: "", role: "",
    is_active: true, welcome_message: "Bienvenue dans l'équipe Jamora !",
    whatsapp_number: forCollaborator?.whatsapp || "",
  });

  useEffect(() => {
    api.get("/users/role-options").then(({ data }) => {
      const assignable = data.filter((r) => r.assignable);
      // No role is ever preselected: the person creating the account chooses it explicitly.
      setRoles(assignable);
    });
  }, []);

  // The job functions of the fiche may suggest a role, shown next to the field, never preselected.
  const [roleSuggestions, setRoleSuggestions] = useState([]);
  useEffect(() => {
    const ids = forCollaborator?.job_function_ids || [];
    if (!ids.length) return;
    api.get("/job-functions").then(({ data }) => setRoleSuggestions(suggestedRoles(data.items, ids))).catch(() => setRoleSuggestions([]));
  }, [forCollaborator]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.role) {
      toast.error("Choisissez le rôle de ce compte");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    if (withFiche && !isFicheDraftValid(ficheDraft, ficheDraft.relation_type || "x")) {
      toast.error("Complétez les informations de la fiche (type de relation, date d'entrée).");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/users", form);
      if (forCollaborator) {
        try {
          await api.put(`/collaborators/${forCollaborator.id}/user-link`, { expected_version: forCollaborator.version, user_id: data.id });
          toast.success("Compte créé et rattaché à la fiche");
        } catch (err) {
          toast.warning("Compte créé, mais le rattachement à la fiche a échoué : rattachez-le depuis la fiche.");
        }
        navigate(`/collaborateurs/${forCollaborator.id}`);
        return;
      }
      if (withFiche) {
        const parts = form.name.trim().split(/\s+/);
        const contacts = { email: form.email.trim().toLowerCase() };
        if (form.whatsapp_number.trim()) contacts.whatsapp = form.whatsapp_number.trim();
        if (ficheDraft.phone.trim()) contacts.phone = ficheDraft.phone.trim();
        try {
          const type = ficheDraft.relation_type || (await api.get("/collaborators/referentials")).data.relation_types.find((r) => r.active && r.code === "salarie")?.code || (await api.get("/collaborators/referentials")).data.relation_types.find((r) => r.active)?.code;
          const created = await api.post("/collaborators", {
            first_name: parts.length > 1 ? parts.slice(0, -1).join(" ") : null, last_name: parts[parts.length - 1], relation_type: type,
            job_title: ficheDraft.job_title.trim() || null,
            status: ficheDraft.status, entry_date: ficheDraft.status === "active" ? ficheDraft.entry_date : null,
            service_id: ficheDraft.service_id || null, manager_id: ficheDraft.manager_id || null, manager_user_id: ficheDraft.manager_user_id || null,
            contacts, link_user_id: data.id,
          }, { headers: { "Idempotency-Key": ficheKey } });
          toast.success("Utilisateur et fiche collaborateur créés");
          navigate(`/collaborateurs/${created.data.id}`);
        } catch (err) {
          // the account exists: finish the fiche on the dedicated page, which handles duplicates and shared contacts
          toast.warning("Utilisateur créé, mais la fiche n'a pas pu l'être : complétez-la ici.");
          navigate("/collaborateurs/nouveau", { state: { fromUser: { id: data.id, name: form.name, email: form.email, whatsapp: form.whatsapp_number } } });
        }
        return;
      }
      if (data.is_active) {
        if (data.invite_email_sent) {
          toast.success("Utilisateur créé. L'e-mail d'invitation a été envoyé.");
        } else {
          toast.warning("Utilisateur créé, mais l'email d'invitation n'a pas pu être envoyé. Communiquez le mot de passe directement.");
        }
      } else {
        toast.success("Utilisateur créé (compte inactif)");
      }
      navigate(hasPermission(me, "users:view") ? "/users" : "/profile");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="user-form-page">
      <Link to={hasPermission(me, "users:view") ? "/users" : "/profile"} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Retour
      </Link>
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Créer un utilisateur</h1>
        <p className="text-sm text-slate-500">Ajoutez un nouveau membre à votre équipe.</p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 items-start">
        <div className="space-y-4">
          <Card className="p-5 space-y-4">
            <div className="font-display font-semibold text-sm text-slate-900">Informations personnelles</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="user-name">Nom complet *</Label>
                <Input
                  id="user-name"
                  data-testid="input-users-name"
                  required
                  placeholder="Marie Dupont"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="user-email">Adresse email *</Label>
                <Input
                  id="user-email"
                  data-testid="input-users-email"
                  type="email"
                  required
                  placeholder="marie.dupont@entreprise.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <div className="font-display font-semibold text-sm text-slate-900">Compte et accès</div>
            <div className="space-y-2">
              <Label htmlFor="user-password">Mot de passe *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="user-password"
                    data-testid="input-users-password"
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={8}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    data-testid="btn-toggle-password"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setForm((f) => ({ ...f, password: generatePassword() }));
                    setShowPassword(true);
                  }}
                  data-testid="btn-generate-password"
                  className="flex-shrink-0"
                >
                  <RefreshCw size={14} className="mr-1.5" /> Générer
                </Button>
              </div>
              <p className="text-xs text-slate-500">Minimum 8 caractères avec au moins une lettre et un chiffre.</p>
            </div>
            <div className="space-y-2">
              <Label>Rôle *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="select-users-role">
                  <SelectValue placeholder="Choisir un rôle" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <SuggestedRoleHint suggestions={roleSuggestions} selectedRole={form.role} assignable={roles.map((r) => r.name)}
                onUse={(role) => setForm((f) => ({ ...f, role }))} />
            </div>
            <div className="flex items-start gap-3 bg-jamora/5 border border-jamora/10 rounded-lg p-3">
              <Mail size={16} className="text-jamora flex-shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600">
                Les permissions sont définies par le rôle.
                {me?.role === "admin" && <> Vous pouvez personnaliser les rôles depuis la page{" "}<Link to="/roles" className="text-jamora font-medium hover:underline">Rôles & permissions</Link>.</>}
              </p>
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <div className="font-display font-semibold text-sm text-slate-900">Statut</div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-slate-900">Compte actif</div>
                <div className="text-xs text-slate-500">Un utilisateur désactivé ne pourra plus se connecter à la plateforme.</div>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="switch-user-active"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-whatsapp">Numéro WhatsApp (optionnel)</Label>
              <Input
                id="user-whatsapp"
                data-testid="input-user-whatsapp"
                placeholder="+221 77 123 45 67"
                value={form.whatsapp_number}
                onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
              />
              <p className="text-xs text-slate-500">
                Si renseigné, l'utilisateur reçoit aussi une notification WhatsApp de bienvenue.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-welcome">Message d'accueil (optionnel)</Label>
              <Textarea
                id="user-welcome"
                data-testid="input-welcome-message"
                rows={2}
                value={form.welcome_message}
                onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
              />
              <p className="text-xs text-slate-500">Ce message sera affiché dans l'email d'invitation.</p>
            </div>
          </Card>

          {collabOn && (
            <Card className="p-5 space-y-4" data-testid="fiche-card">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-slate-900">Créer aussi une fiche collaborateur</div>
                  <div className="text-xs text-slate-500">La fiche est rattachée à ce compte en même temps.{form.is_active ? "" : " Impossible tant que le compte est inactif."}</div>
                </div>
                <Switch checked={withFiche && form.is_active} disabled={!form.is_active} onCheckedChange={setWithFiche} data-testid="switch-create-fiche" />
              </div>
              {withFiche && form.is_active && <FicheFieldsForUser draft={ficheDraft} onChange={setFicheDraft} />}
            </Card>
          )}

          {forCollaborator && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="for-collaborator-banner">
              Ce compte sera rattaché à la fiche de <strong>{forCollaborator.name}</strong> dès sa création.
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate("/users")}>Annuler</Button>
            <Button
              type="submit"
              data-testid="btn-users-create-submit"
              disabled={creating || roles.length === 0 || !form.role}
            >
              {creating ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
              Créer l'utilisateur
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <Card className="p-4 flex gap-3">
            <div className="w-9 h-9 rounded-lg bg-jamora/10 text-jamora flex items-center justify-center flex-shrink-0">
              <Mail size={16} />
            </div>
            <div>
              <div className="text-sm font-medium text-slate-900">Invitation automatique</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Un email sera envoyé à l'utilisateur avec ses identifiants et un lien de connexion.
              </div>
            </div>
          </Card>

          <Card className="p-4 flex gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={16} />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900">Sécurité</div>
              <ul className="mt-1.5 space-y-1.5">
                {[
                  "Le mot de passe est automatiquement haché",
                  "L'utilisateur doit changer son mot de passe à la première connexion",
                  "Le compte peut être désactivé à tout moment",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-1.5 text-xs text-slate-600">
                    <Check size={13} className="text-emerald-600 flex-shrink-0 mt-0.5" /> {line}
                  </li>
                ))}
              </ul>
            </div>
          </Card>

          <Card className="p-4 flex gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center flex-shrink-0">
              <Lightbulb size={16} />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900">Bonnes pratiques</div>
              <ul className="mt-1.5 space-y-1.5 list-disc list-inside">
                {[
                  "Utilisez une adresse email professionnelle",
                  "Attribuez uniquement les permissions nécessaires",
                  "Vous pourrez modifier le rôle de l'utilisateur plus tard",
                  "Un utilisateur désactivé conserve ses données (tâches, prospects)",
                ].map((line) => (
                  <li key={line} className="text-xs text-slate-600">{line}</li>
                ))}
              </ul>
            </div>
          </Card>
        </div>
      </form>
    </div>
  );
}
